import React, {useRef, useLayoutEffect} from "react";
import {InputPropsInterface} from "./Input.props.interface";
import Message from "../Message";
import InfinityIcon from '../../assets/icons/16/infinity.svg';
import ClearIcon from '../../assets/icons/16/cross.svg';
import styles from './Input.module.scss';

const Input: React.FC<InputPropsInterface & { isClearable?: boolean }> = (
    {
        inputRef,
        label = undefined,
        size = 'medium',
        error = undefined,
        className = undefined,
        prefix,
        suffix,
        isInfinity = false,
        isClearable = false,
        onChange,
        value,
        ...rest
    }) => {
    const _inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLLabelElement | null>(null);
    const prefixRef = useRef<HTMLDivElement | null>(null);
    const suffixRef = useRef<HTMLDivElement | null>(null);
    const actualRef = (node: HTMLInputElement) => {
        _inputRef.current = node;
        if (typeof inputRef === 'function') inputRef(node);
        else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };

    const isInfinitySelected = value === Infinity
    const hasValue = value !== undefined && value !== '' && value !== null

    useLayoutEffect(() => {
        const pW = prefixRef.current?.offsetWidth ?? 0
        const sW = suffixRef.current?.offsetWidth ?? 0

        if (containerRef.current) {
            containerRef.current.style.setProperty('--prefix-width', `${pW}px`);
            containerRef.current.style.setProperty('--suffix-width', `${sW}px`);
        }
    }, [prefix, suffix, size, isInfinity, value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
    };

    const handleSyntheticChange = (val: string | number) => {
        if (!_inputRef.current) return;
        onChange?.({
            ...({} as React.SyntheticEvent),
            target: {..._inputRef.current, value: val},
            currentTarget: _inputRef.current,
        } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleClear = () => handleSyntheticChange('')

    return (
        <label
            htmlFor={rest.id}
            ref={containerRef}
            className={[
                styles.container,
                error && styles.error,
                isInfinity && styles.isInfinity,
                isInfinitySelected && styles.isInfinitySelected,
                styles[size],
                prefix && styles.hasPrefix,
                (suffix || isInfinity || (isClearable && hasValue && !isInfinitySelected)) && styles.hasSuffix,
                className,
            ].filter(Boolean).join(' ')}>
            {label}
            <div className={styles.wrapper}>
                {prefix && (
                    <div className={styles.icon} ref={prefixRef}>
                        {prefix}
                    </div>
                )}
                <input
                    {...rest}
                    ref={actualRef}
                    type={isInfinity ? 'number' : rest.type}
                    placeholder={isInfinity && isInfinitySelected ? '' : rest.placeholder}
                    readOnly={(isInfinity && isInfinitySelected) || rest.readOnly}
                    onChange={handleChange}
                    value={isInfinitySelected ? '' : (value ?? '')}
                />
                {isInfinity && isInfinitySelected && (
                    <div className={styles.infinity}>Не ограничено</div>
                )}
                {(suffix || isInfinity || (isClearable && hasValue && !isInfinitySelected)) && (
                    <div
                        className={styles.icon}
                        ref={suffixRef}
                        onClick={
                            isInfinity
                                ? isInfinitySelected
                                    ? () => handleSyntheticChange('')
                                    : () => handleSyntheticChange(Infinity)
                                : isClearable && hasValue
                                    ? handleClear
                                    : undefined
                        }>
                        {isInfinity ? (
                            <InfinityIcon className={isInfinitySelected ? styles.isActiveInfinity : ''}/>
                        ) : isClearable && hasValue && !isInfinitySelected ? (
                            <ClearIcon/>
                        ) : (
                            suffix
                        )}
                    </div>
                )}
            </div>
            {typeof error === 'string' && error.trim() && <Message>{error}</Message>}
        </label>
    );
};

export default Input;
